import Cookies from 'js-cookie';
import {
  AIDT_ACCESS_TOKEN,
  AIDT_ACCESS_ID,
  AIDT_LOGIN_AUTH_PARTNER_ID,
  LOGIN_AUTH_TOKEN_KEY,
} from '@shared/constants/token';
import { addError } from '@shared/utils/apm';
import { createAuthAxios, getAuthHeaders } from '@shared/utils/createAuthAxios';
import { refreshAccessToken, handleRefreshFailure } from '@shared/utils/tokenRefreshManager';
import {
  isDashboardLogTypeInput,
  isXApiActionData,
  LRS_DATA_CATEGORIES,
  StudyEventLogInput,
  StudyEventRequestInput,
  StudyEventRequestSessionInput,
} from './type';
import delay from '@shared/utils/delay';
import { XApiActionData } from '@ui/utils/ActivityManager/Action';
import { isNil, omitBy } from 'lodash-es';

function chunkByCondition<T>(
  array: T[],
  condition1: (item: T) => boolean,
  condition2: (item: T) => boolean,
): [StudyEventRequestSessionInput[], StudyEventRequestInput[], XApiActionData[]] {
  const appSessionLogs: StudyEventRequestSessionInput[] = [];
  const studyEventLogs: StudyEventRequestInput[] = [];
  const xApiDataLogs: XApiActionData[] = [];

  array.forEach((item) => {
    if (condition1(item)) {
      xApiDataLogs.push(item as XApiActionData);
    } else if (condition2(item)) {
      appSessionLogs.push(item as StudyEventRequestSessionInput);
    } else {
      studyEventLogs.push(item as StudyEventRequestInput);
    }
  });
  return [appSessionLogs, studyEventLogs, xApiDataLogs];
}

//TODO:
const FETCH_URL =
  `${process.env.NEXT_PUBLIC_STUDY_EVENT_LOG_API_URL}/study-event-log` ??
  'https://dev.english.management/study-event-log';

export const X_API_FETCH_URL =
  `${process.env.NEXT_PUBLIC_X_API_URL}/learning-record-store` ??
  `https://dev.english.management/learning-record-store`;

/**
 * 401(토큰 만료) 시 refreshAccessToken으로 토큰을 갱신한 뒤 1회 재시도하는 fetch 래퍼.
 *
 * createAuthAxios 응답 인터셉터와 동일한 인증 복구 전략을, fetch의 `keepalive`를 유지한 채 적용한다.
 * study-event-log/LRS는 페이지 이탈(beforeunload/pagehide) 중에도 마지막 로그 전송을 보장하기 위해
 * `keepalive: true`가 필수이며, 이는 XHR 기반 axios로는 불가능하므로 fetch를 유지한다.
 *
 * authorization 헤더는 이 함수가 주입하므로, 호출부는 그 외 헤더만 전달한다.
 * `...init`을 재시도에도 그대로 전개하여 method/body/keepalive/mode 등이 보존된다.
 */
async function authFetch(url: string, init: RequestInit): Promise<Response> {
  const baseHeaders = { ...(init.headers as Record<string, string> | undefined) };

  const response = await fetch(url, {
    ...init,
    headers: { ...baseHeaders, ...getAuthHeaders() },
  });

  // 401이 아니거나 미로그인 상태(토큰 없음)면 그대로 반환
  if (response.status !== 401 || !Cookies.get(LOGIN_AUTH_TOKEN_KEY)) {
    return response;
  }

  // 토큰 만료 → 갱신 후 1회 재시도. refreshAccessToken은 Apollo errorLink/axios 인터셉터와
  // 공유되는 모듈이라 동시 다발 401이 isRefreshing 플래그로 한 번의 refresh로 합쳐진다.
  const result = await refreshAccessToken();
  if (result.success) {
    return fetch(url, {
      ...init,
      headers: { ...baseHeaders, authorization: `Bearer ${result.token}` },
    });
  }

  // refresh token 자체가 무효(auth) → 로그아웃 처리. transient(네트워크/5xx)면 원래 401 응답 반환.
  if (result.reason === 'auth') {
    handleRefreshFailure();
  }
  return response;
}

export async function sendFetchStrategy<T extends StudyEventLogInput>(data: T) {
  const [appSessionLogs, studyEventLogs, xApiDataLogs] = chunkByCondition(
    data.studyEventLogs,
    (event) => isXApiActionData(event),
    (event) => isDashboardLogTypeInput(event),
  );

  const partnerId = Cookies.get(AIDT_LOGIN_AUTH_PARTNER_ID) ?? '';

  if (xApiDataLogs && xApiDataLogs.length > 0 && partnerId) {
    await sendXApiData(
      JSON.stringify(
        xApiDataLogs.map((log) => {
          return {
            category: LRS_DATA_CATEGORIES[log.type],
            partnerId,
            data: log.payload,
          };
        }),
      ),
    );
  }

  if (studyEventLogs && studyEventLogs.length > 0) {
    const response = await authFetch(FETCH_URL, {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        studyEventLogs,
      }),
      keepalive: true,
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // 토큰 갱신 재시도 후에도 실패하면 해당 로그는 유실된다(큐 복구는 2단계 예정).
    // 유실 발생을 Datadog에서 관측할 수 있도록 기록한다.
    if (!response.ok) {
      addError(new Error('study-event-log transfer failed'), {
        'error.type': 'StudyEventLogTransferError',
        'error.status': response.status,
        'studyEventLog.count': studyEventLogs.length,
      });
    }
  }

  await delay(50);
  if (appSessionLogs && appSessionLogs.length > 0) {
    for (const appSessionLog of appSessionLogs) {
      await sendAppSession(appSessionLog);
    }
  }
}

const instance = createAuthAxios(`${process.env.NEXT_PUBLIC_APP_SESSION_API_URL}`);

export async function sendAppSession<T extends any>(data: T) {
  try {
    if (!process.env.NEXT_PUBLIC_APP_SESSION_API_URL) {
      return;
    }
    await instance.post(
      `/app-session`,
      {
        logs: {
          ...(data ?? {}),
        },
      },
    );
  } catch (e: any) {
    addError(new Error(`app-session`), {
      'error.type': 'AppSessionError',
      'error.message': e.message,
      'error.stack': e.stack,
      'error.name': e.name,
      'error.code': e.code,
      'error.description': e.description,
    });
  }
}

export async function sendXApiData(body: any) {
  // if (isAIDTUser()) {
  //   return;
  // }

  const response = await authFetch(X_API_FETCH_URL, {
    method: 'POST',
    body,
    keepalive: true,
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      ...omitBy(
        {
          'Aidt-Access-Id': Cookies.get(AIDT_ACCESS_ID),
          'Aidt-Token': Cookies.get(AIDT_ACCESS_TOKEN),
          'Partner-Id': Cookies.get(AIDT_LOGIN_AUTH_PARTNER_ID),
        },
        isNil,
      ),
    },
  });

  if (!response.ok) {
    addError(new Error('x-api(LRS) transfer failed'), {
      'error.type': 'XApiTransferError',
      'error.status': response.status,
    });
  }
}
